import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { PublicLayout } from '../../components/PublicLayout';
import { PublicPageSeo } from '../../components/PublicPageSeo';
import { RelatedLinks } from '../../components/RelatedLinks';
import { getContent, PlatformDownload } from '../../content/i18n';
import { useLocale, useLocalizedPath } from '../../hooks/useLocale';
import { getClientDownloadUrl, getMacQuarantineCommand } from '../../utils/clientDownloads';

const VALID_PLATFORMS = ['windows', 'macos', 'android', 'ios'] as const;
type PlatformId = typeof VALID_PLATFORMS[number];

export const DownloadPlatformPage: React.FC = () => {
  const { platform } = useParams<{ platform: string }>();
  const locale = useLocale();
  const localizedPath = useLocalizedPath();
  const content = getContent(locale);

  if (!platform || !VALID_PLATFORMS.includes(platform as PlatformId)) {
    return <Navigate to={localizedPath('/download')} replace />;
  }

  const platformData: PlatformDownload = content.platforms[platform as PlatformId];
  const downloadUrl = getClientDownloadUrl(platform as PlatformId);
  const homePath = locale === 'en' ? '/en' : '/';
  const downloadPath = locale === 'en' ? '/en/download' : '/download';
  const pagePath = locale === 'en' ? `/en/download/${platform}` : `/download/${platform}`;

  return (
    <>
      <PublicPageSeo
        breadcrumbs={[
          { name: content.common.home, path: homePath },
          { name: content.download.title, path: downloadPath },
          { name: platformData.name, path: pagePath },
        ]}
      />
      <PublicLayout
        breadcrumbs={[
          { label: content.common.home, href: localizedPath('/') },
          { label: content.download.title, href: localizedPath('/download') },
          { label: platformData.name },
        ]}
      >
        <section className="py-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {platformData.name} {locale === 'zh' ? '跨境客户端配置' : 'Cross-Border Client Setup'}
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mb-8">
            {locale === 'zh' ? '系统要求：' : 'Requirements: '}{platformData.requirements}
          </p>

          {downloadUrl ? (
            <div className="mb-10 p-6 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {locale === 'zh' ? '下载 NodeUnion 官方客户端' : 'Download NodeUnion Official Client'}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                {locale === 'zh'
                  ? '登录账户即可自动同步节点，适用于跨境电商、TikTok 运营与海外 AI 工具等场景。'
                  : 'Sign in to sync nodes automatically for e-commerce, TikTok, and overseas AI tools.'}
              </p>
              <a
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                {locale === 'zh' ? '立即下载' : 'Download Now'}
              </a>
              {platform === 'macos' && (
                <div className="mt-4">
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                    {locale === 'zh'
                      ? '安装后若提示无法打开，请在终端执行以下命令：'
                      : 'If macOS blocks the app, run in Terminal:'}
                  </p>
                  <code className="block text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded mb-2 overflow-x-auto">
                    {getMacQuarantineCommand()}
                  </code>
                </div>
              )}
            </div>
          ) : (
            <div className="mb-10 p-6 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {locale === 'zh' ? 'iOS 配置说明' : 'iOS Setup'}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                {locale === 'zh'
                  ? '请注册并登录用户中心，在「使用教程」中查看 iOS 配置指南，或联系在线客服获取支持。'
                  : 'Register and sign in to the user center for iOS setup guides, or contact support for assistance.'}
              </p>
              <Link
                to={localizedPath('/login')}
                className="inline-block bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                {content.common.login}
              </Link>
            </div>
          )}

          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            {locale === 'zh' ? '配置步骤' : 'Setup Steps'}
          </h2>
          <ol className="list-decimal list-inside space-y-3 mb-10 text-gray-600 dark:text-gray-300">
            {platformData.steps.map((step, i) => (
              <li key={i} className="leading-relaxed">{step}</li>
            ))}
          </ol>

          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            {locale === 'zh' ? '故障排查' : 'Troubleshooting'}
          </h2>
          <div className="space-y-4 mb-10">
            {platformData.troubleshooting.map((item) => (
              <div key={item.issue} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                <p className="font-medium text-gray-900 dark:text-white mb-1">{item.issue}</p>
                <p className="text-gray-600 dark:text-gray-300 text-sm">{item.fix}</p>
              </div>
            ))}
          </div>

          <Link
            to={localizedPath('/register')}
            className="inline-block bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-lg font-medium transition-colors"
          >
            {content.common.register}
          </Link>
        </section>

        <RelatedLinks
          links={[
            { label: locale === 'zh' ? '下载中心' : 'Download Center', href: '/download' },
            { label: locale === 'zh' ? '帮助中心' : 'Help Center', href: '/help' },
            { label: locale === 'zh' ? '套餐价格' : 'Pricing', href: '/pricing' },
          ]}
        />
      </PublicLayout>
    </>
  );
};
